/**
 * sun-collection-nav-finishes.js — Web Component <sun-collection-nav-finishes>
 *
 * Inline pill buttons mirroring a product-option filter (default: "finition")
 * in the collection nav. Delegates state to the native Shopify filter by
 * toggling the matching <input type="checkbox"> inside the sidebar filter
 * form. The existing Broadcast collection-filters-form handler picks up the
 * synthetic input event and runs the usual fetch + re-render flow.
 *
 * After the sidebar is replaced by that flow, a MutationObserver re-syncs
 * the pills' active/disabled state from the fresh DOM. No modification to
 * Broadcast JS is required.
 *
 * Scope: rendered inside sections/sun-collection.liquid. Loaded from there.
 */
if (!customElements.get('sun-collection-nav-finishes')) {
  class SunCollectionNavFinishes extends HTMLElement {
    connectedCallback() {
      this.paramName = this.dataset.paramName;
      this.buttons = Array.from(this.querySelectorAll('.sun-collection-nav-finishes__btn'));
      this.sidebar = this.#findSidebar();

      this.onClick = (event) => this.#handleClick(event);
      for (const btn of this.buttons) btn.addEventListener('click', this.onClick);

      if (this.sidebar) {
        this.#hideSidebarCounterpart();
        this.observer = new MutationObserver(() => this.#onSidebarMutation());
        this.observer.observe(this.sidebar, { childList: true });
      }
    }

    disconnectedCallback() {
      for (const btn of this.buttons) btn.removeEventListener('click', this.onClick);
      this.observer?.disconnect();
    }

    #findSidebar() {
      const section = this.closest('[data-section-id]');
      return section?.querySelector('[data-collection-sidebar]') ?? null;
    }

    #findCheckbox(value) {
      if (!this.sidebar || !this.paramName) return null;
      const selector = `input[type="checkbox"][name="${this.paramName}"][value="${CSS.escape(value)}"]`;
      return this.sidebar.querySelector(selector);
    }

    #handleClick(event) {
      const btn = event.currentTarget;
      if (btn.disabled || btn.classList.contains('is-disabled')) return;

      const value = btn.dataset.finishValue;
      const checkbox = this.#findCheckbox(value);
      if (!checkbox) return;

      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event('input', { bubbles: true }));
    }

    #resyncFromSidebar() {
      for (const btn of this.buttons) {
        const checkbox = this.#findCheckbox(btn.dataset.finishValue);
        if (!checkbox) continue;

        const isActive = checkbox.checked;
        const isDisabled = checkbox.disabled && !isActive;

        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        btn.classList.toggle('is-disabled', isDisabled);
        btn.disabled = isDisabled;
      }
    }

    #hideSidebarCounterpart() {
      if (!this.sidebar || !this.paramName) return;
      const anyCheckbox = this.sidebar.querySelector(
        `input[type="checkbox"][name="${this.paramName}"]`
      );
      const group = anyCheckbox?.closest('.filter-group');
      group?.classList.add('sun-collection-nav-finishes__sidebar-hidden');
    }

    #onSidebarMutation() {
      this.#hideSidebarCounterpart();
      this.#resyncFromSidebar();
    }
  }

  customElements.define('sun-collection-nav-finishes', SunCollectionNavFinishes);
}
